class HeroBannerFloatingButton {
  constructor() {
    this.originalButton = null;
    this.floatingButton = null;
    this.hasFlavorsSelected = false;
    this.config = {};
    this.init();
  }

  init() {
    this.originalButton = document.querySelector('.hero-banner__button');
    if (!this.originalButton) return;

    this.loadConfig();
    this.createFloatingButton();
    this.bindEvents();
    this.updateButtonState();
    this.onScroll();
  }

  loadConfig() {
    const btn = this.originalButton;
    this.config = {
      state1Text: btn.dataset.floatingState1Text || 'Select flavors',
      state2Text: btn.dataset.floatingState2Text || 'Add to cart',
      borderRadius: btn.dataset.floatingBorderRadius || '12',
      fontSize: btn.dataset.floatingFontSize || '17',
      paddingH: btn.dataset.floatingPaddingH || '24',
      paddingV: btn.dataset.floatingPaddingV || '12'
    };
  }

  createFloatingButton() {
    this.floatingButton = document.createElement('button');
    this.floatingButton.className = 'hero-banner__button hero-banner__button--floating hero-banner-button-hidden';
    this.floatingButton.type = 'button';
    this.applyBaseStyles();
    document.body.appendChild(this.floatingButton);
  }

  applyBaseStyles() {
    const style = this.floatingButton.style;
    style.borderRadius = `${this.config.borderRadius}px`;
    style.fontSize = `${this.config.fontSize}px`;
    style.padding = `${this.config.paddingV}px ${this.config.paddingH}px`;
    style.border = 'none';
    style.fontWeight = '500';
    style.fontFamily = 'var(--font-body-family)';
  }

  bindEvents() {
    window.addEventListener('scroll', this.onScroll.bind(this), { passive: true });
    document.addEventListener('flavors:changed', this.onFlavorsChanged.bind(this));
    this.floatingButton.addEventListener('click', this.onFloatingButtonClick.bind(this));
    this.originalButton.addEventListener('click', this.onHeroButtonClick.bind(this));
  }

  onHeroButtonClick(e) {
    if (!this.originalButton.getAttribute('href')) {
      e.preventDefault();
      const target = document.querySelector('.product__featured-image-title');
      if (target) {
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  }

  onFlavorsChanged(e) {
    this.hasFlavorsSelected = e.detail.hasFlavors;
    this.updateButtonState();
  }

  updateButtonState() {
    const style = this.floatingButton.style;
    if (this.hasFlavorsSelected) {
      this.floatingButton.textContent = this.config.state2Text;
      this.floatingButton.setAttribute('aria-label', this.config.state2Text);
      this.floatingButton.classList.remove('floating-button--disabled');
      this.floatingButton.classList.add('floating-button--active');
      style.background = '#008330';
      style.color = '#FFFFFF';
      style.cursor = 'pointer';
    } else {
      this.floatingButton.textContent = this.config.state1Text;
      this.floatingButton.setAttribute('aria-label', this.config.state1Text);
      this.floatingButton.classList.add('floating-button--disabled');
      this.floatingButton.classList.remove('floating-button--active');
      style.background = 'linear-gradient(135deg, #FFEED0 0%, #FFFFFF 32%, #E3EEF6 100%)';
      style.color = '#000000';
      style.cursor = 'pointer';
    }
  }

  onFloatingButtonClick(e) {
    e.preventDefault();
    if (this.hasFlavorsSelected) {
      this.addToCart();
    } else {
      this.scrollToFlavors();
    }
  }

  scrollToFlavors() {
    const flavorSection = document.querySelector('.biobuybox__flavors--am') || document.querySelector('.biobuybox') || document.querySelector('.product-upsell') || document.querySelector('[id^="ProductInfo-"]');
    if (flavorSection) {
      const headerOffset = 110;
      const top = flavorSection.getBoundingClientRect().top + window.scrollY - headerOffset;
      window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
    }
  }

  async addToCart() {
    if (this.isAddingToCart) return;
    this.isAddingToCart = true;
    this.floatingButton.textContent = 'Adding...';

    try {
      const upsellInstance = window.productUpsellInstance;
      if (upsellInstance && typeof upsellInstance.addProductsToCart === 'function') {
        await upsellInstance.addProductsToCart();
      } else {
        const visibleFlavors = this.getVisibleSelectedFlavors();
        if (visibleFlavors.length === 0) {
          throw new Error('No flavors selected');
        }
        const items = visibleFlavors.map(flavor => ({
          id: parseInt(flavor.variantId, 10),
          quantity: flavor.quantity || 1
        }));
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items })
        });
        if (!response.ok) throw new Error('Failed to add to cart');
        document.dispatchEvent(new CustomEvent('cart:update', { bubbles: true }));
      }
    } catch (error) {
      console.error('Add to cart error:', error);
    } finally {
      this.isAddingToCart = false;
      this.updateButtonState();
    }
  }

  getVisibleSelectedFlavors() {
    const flavors = [];
    const elements = document.querySelectorAll('.product-upsell .flavor-card.selected');
    elements.forEach(card => {
      const btn = card.querySelector('.add-flavor-button, .flavor-quantity-controls');
      if (!btn) return;
      const variantId = btn.getAttribute('data-variant-id');
      const quantityEl = card.querySelector('.flavor-quantity-display');
      const quantity = quantityEl ? parseInt(quantityEl.textContent, 10) : 1;
      if (variantId) {
        flavors.push({ variantId, quantity });
      }
    });
    return flavors;
  }

  onScroll() {
    const heroSection = document.querySelector('.hero-banner');
    const productContentWrappers = document.querySelectorAll('.product__content-wrapper, .biobuybox');
    const viewportHeight = window.innerHeight;
    const halfViewport = viewportHeight / 2;

    if (heroSection) {
      const heroRect = heroSection.getBoundingClientRect();
      if (heroRect.bottom > halfViewport) {
        requestAnimationFrame(this.hide.bind(this));
        return;
      }
    }

    for (const wrapper of productContentWrappers) {
      const wrapperRect = wrapper.getBoundingClientRect();
      if (wrapperRect.top < viewportHeight && wrapperRect.bottom > halfViewport) {
        requestAnimationFrame(this.hide.bind(this));
        return;
      }
    }

    requestAnimationFrame(this.reveal.bind(this));
  }

  hide() {
    this.floatingButton.classList.add('hero-banner-button-hidden');
  }

  reveal() {
    this.floatingButton.classList.remove('hero-banner-button-hidden');
  }
}

document.addEventListener('DOMContentLoaded', () => {
  new HeroBannerFloatingButton();
});
